import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import i18next from 'i18next'
import {ApiError, ApiService} from '@/services/api'
import type {Project, Tag, Task, TaskInstance, TaskWithInstance} from '@backend/types'
import {useTelegramStore} from './telegram'
import {useLocalStore} from './local'

export const useAppStore = defineStore('app', () => {
  const apiService = ref<ApiService>()
  const allTasks = ref<TaskWithInstance[]>([])
  const projects = ref<Project[]>([])
  const tags = ref<Tag[]>([])
  const searchResults = ref<TaskWithInstance[]>([])
  const isSearching = ref(false)

  function upsertTask(task: TaskWithInstance) {
    const index = allTasks.value.findIndex((item) => item.task_id === task.task_id)
    if (index === -1) {
      allTasks.value.push(task)
      return
    }
    allTasks.value[index] = task
  }

  async function initialize() {
    const telegramStore = useTelegramStore()
    await telegramStore.initialize()

    // Определяем тип авторизации и формируем соответствующие данные
    let authData: string

    if (telegramStore.isTelegramWebApp && telegramStore.initData) {
      // Для WebApp используем initData как есть
      authData = telegramStore.initData
      console.log('🔗 Using WebApp initData for auth')
    } else if (telegramStore.browserAuthData) {
      // Для браузерной авторизации формируем URL parameters как в initData
      const params = new URLSearchParams()
      Object.entries(telegramStore.browserAuthData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })

      authData = params.toString()
      console.log('🌐 Using browser auth data as URL params for auth')
    } else {
      throw new Error('No authentication data available')
    }

    // Initialize API service with backend endpoint and auth data
    apiService.value = new ApiService(import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000', authData)

    try {
      allTasks.value = await apiService.value.getTasks()
      projects.value = await apiService.value.getProjects()
      tags.value = await apiService.value.getTags()
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    }

    // Load user settings and sync language
    try {
      const user = await apiService.value.getUser()
      const serverLang = user.settings?.lang
      if (serverLang && serverLang !== i18next.language) {
        await i18next.changeLanguage(serverLang)
      }
      const localStore = useLocalStore()
      if (serverLang) {
        localStore.lang = serverLang
      }
    } catch (error) {
      console.error('Failed to load user settings:', error)
    }
  }

  async function loadAll() {
    if (!apiService.value) return
    allTasks.value = await apiService.value.getTasks()
    projects.value = await apiService.value.getProjects()
    tags.value = await apiService.value.getTags()
  }

  async function updateTask(taskId: number, updatedTask: Partial<Task>) {
    const result = await apiService.value?.updateTask(taskId, updatedTask)
    if (result) {
      upsertTask(result)
    }
  }

  async function loadTaskById(taskId: number): Promise<TaskWithInstance | null> {
    const existingTask = allTasks.value.find((task) => task.task_id === taskId)
    if (existingTask) {
      return existingTask
    }
    if (!apiService.value) {
      return null
    }

    try {
      const task = await apiService.value.getTask(taskId)
      upsertTask(task)
      return task
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null
      }
      throw error
    }
  }

  async function deleteTask(taskId: number) {
    await apiService.value?.deleteTask(taskId)
    allTasks.value = allTasks.value.filter((task) => task.task_id !== taskId)
  }

  async function updateTaskInstance(taskInstanceId: number, updatedTaskInstance: Partial<TaskInstance>) {
    const result = await apiService.value?.updateTaskInstance(taskInstanceId, updatedTaskInstance)
    if (result) {
      const task = allTasks.value.find((task) => task.task_id === result.task_id)
      if (task) {
        const idx = task.instances.findIndex((inst) => inst.task_instance_id === taskInstanceId)
        if (idx !== -1) {
          task.instances[idx] = result
        }
      }
    }
  }

  async function createTaskInstance(taskId: number, taskInstance: Partial<TaskInstance>) {
    const result = await apiService.value?.createTaskInstance(taskId, taskInstance)
    if (result) {
      const task = allTasks.value.find((task) => task.task_id === taskId)
      if (task) {
        task.instances.push(result)
      }
    }
  }

  async function createTask(task: Partial<Task>, instance?: Partial<TaskInstance>) {
    const result = await apiService.value?.createTask(task)
    if (result) {
      upsertTask(result)
      if (instance) {
        const taskInstance = result.instances[0]
        if (taskInstance) {
          await updateTaskInstance(taskInstance.task_instance_id, instance)
        }
      }
    }
    return result
  }

  async function createProject(project: Partial<Project>) {
    const result = await apiService.value?.createProject(project)
    if (result) {
      projects.value.push(result)
    }
    return result
  }

  async function updateProject(projectId: number, project: Partial<Project>) {
    const result = await apiService.value?.updateProject(projectId, project)
    if (result) {
      const index = projects.value.findIndex((p) => p.project_id === projectId)
      if (index !== -1) {
        projects.value[index] = result
      }
    }
    return result
  }

  async function deleteProject(projectId: number) {
    await apiService.value?.deleteProject(projectId)
    projects.value = projects.value.filter((p) => p.project_id !== projectId)
  }

  async function deleteTaskInstance(taskInstanceId: number) {
    await apiService.value?.deleteTaskInstance(taskInstanceId)
    // Update local state by removing the instance from its task
    for (const task of allTasks.value) {
      const index = task.instances.findIndex((instance) => instance.task_instance_id === taskInstanceId)
      if (index !== -1) {
        task.instances.splice(index, 1)
        break
      }
    }
  }

  async function searchTasks(query: string) {
    if (!apiService.value || !query.trim()) {
      searchResults.value = []
      return
    }
    isSearching.value = true
    try {
      searchResults.value = await apiService.value.searchTasks(query)
    } catch (error) {
      console.error('Failed to search tasks:', error)
      searchResults.value = []
    } finally {
      isSearching.value = false
    }
  }

  const contacts = computed<string[]>(() => {
    return Array.from(new Set(allTasks.value.map((task) => task.delegated_to).filter(Boolean) as string[]))
  })

  return {
    apiService,
    allTasks,
    projects,
    tags,
    searchResults,
    isSearching,
    contacts,
    initialize,
    loadAll,
    updateTask,
    loadTaskById,
    deleteTask,
    updateTaskInstance,
    createTaskInstance,
    createTask,
    createProject,
    updateProject,
    deleteProject,
    deleteTaskInstance,
    searchTasks,
  }
})
